import { computed } from "vue";
import { useHead } from "@vueuse/head";
import type { HotelDetails, RoomDetails } from "@/types/api.types";

export interface SEOMetaData {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export interface StructuredData {
  "@context": string;
  "@type": string;
  [key: string]: any;
}

// Site-wide keywords, merged into every page's own keywords
export const BASE_KEYWORDS = [
  // Brand
  "roomsvile",
  "roomsvile hotels",
  "roomsvile booking",
  "roomsvile apartments",
  // Core booking terms
  "hotel booking",
  "book hotels online",
  "online hotel reservation",
  "cheap hotels",
  "affordable hotels",
  "budget hotels",
  "luxury hotels",
  "best hotel deals",
  "last minute hotel deals",
  "hotel discounts",
  "hotels near me",
  "rooms near me",
  "hotel rooms",
  "book a room",
  "room booking",
  "accommodation",
  "lodging",
  "places to stay",
  "where to stay",
  "resorts",
  "beach resorts",
  "boutique hotels",
  "guest houses",
  "motels",
  "inns",
  "hostels",
  "serviced apartments",
  "furnished apartments",
  "short let apartments",
  "shortlet",
  "short stay apartments",
  "apartment rental",
  "vacation rentals",
  "holiday homes",
  "holiday rentals",
  "studio apartments",
  "one bedroom apartment",
  "two bedroom apartment",
  "family rooms",
  "suites",
  "executive suites",
  "business hotels",
  "airport hotels",
  "conference hotels",
  "event venues",
  "weekend getaway",
  "staycation",
  "honeymoon hotels",
  "romantic getaways",
  "family friendly hotels",
  "pet friendly hotels",
  "hotels with pool",
  "hotels with free wifi",
  "hotels with parking",
  "hotels with breakfast",
  "24 hour power hotels",
  "secure hotels",
  "verified hotels",
  "hotel reviews",
  "compare hotel prices",
  "instant booking",
  "pay on arrival hotels",
  "free cancellation hotels",
  "travel",
  "travel deals",
  "trip planning",
  "tourism",
  // Nigeria
  "hotels in nigeria",
  "hotel booking nigeria",
  "book hotels in nigeria",
  "cheap hotels in nigeria",
  "luxury hotels in nigeria",
  "short let apartments in nigeria",
  "serviced apartments nigeria",
  "nigeria travel",
  "nigeria accommodation",
  "hotels in lagos",
  "short let lagos",
  "hotels in lekki",
  "short let lekki",
  "hotels in victoria island",
  "hotels in ikoyi",
  "hotels in ikeja",
  "hotels near lagos airport",
  "hotels in abuja",
  "short let abuja",
  "hotels in wuse",
  "hotels in maitama",
  "hotels in garki",
  "hotels in port harcourt",
  "hotels in ibadan",
  "hotels in kano",
  "hotels in kaduna",
  "hotels in enugu",
  "hotels in benin city",
  "hotels in calabar",
  "hotels in uyo",
  "hotels in owerri",
  "hotels in asaba",
  "hotels in warri",
  "hotels in jos",
  "hotels in abeokuta",
  "hotels in ilorin",
  "hotels in akure",
  "hotels in onitsha",
  "hotels in awka",
  "hotels in osogbo",
  "hotels in ado ekiti",
  "hotels in yola",
  "hotels in makurdi",
  "hotels in lokoja",
  "hotels in minna",
  "hotels in bauchi",
  "hotels in sokoto",
  "hotels in maiduguri",
  "hotels in abakaliki",
  "hotels in umuahia",
  "hotels in yenagoa",
];

function mergeKeywords(pageKeywords?: string): string {
  const page = (pageKeywords || "")
    .split(",")
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set([...page, ...BASE_KEYWORDS])).join(", ");
}

export const SITE_NAME = "Roomsvile";
export const SITE_URL = process.env.VUE_APP_SITE_URL || "https://roomsvile.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/default-og.jpg`;

// Social crawlers reject mixed-content images, and Cloudinary serves https too
const toHttps = (url: string) => url.replace(/^http:\/\//, "https://");

// Search engines truncate descriptions around 160 characters
export function truncate(text: string, max = 160): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
}

// Builds the head object for a page; robots/noindex is handled globally in
// App.vue from route meta, so it isn't set here.
export function buildSEOHead(
  metadata: SEOMetaData,
  structuredData?: StructuredData | StructuredData[]
) {
  const fullTitle = metadata.title.includes(SITE_NAME)
    ? metadata.title
    : `${metadata.title} | ${SITE_NAME}`;

  const fullUrl = metadata.url || SITE_URL;
  const fullImage = toHttps(metadata.image || DEFAULT_OG_IMAGE);

  const metaTags = [
    // Primary Meta Tags
    { name: "description", content: metadata.description },
    { name: "author", content: metadata.author || SITE_NAME },

    // Keywords
    { name: "keywords", content: mergeKeywords(metadata.keywords) },

    // Open Graph / Facebook
    { property: "og:type", content: metadata.type || "website" },
    { property: "og:url", content: fullUrl },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: metadata.description },
    { property: "og:image", content: fullImage },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: "en_NG" },

    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:url", content: fullUrl },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: metadata.description },
    { name: "twitter:image", content: fullImage },
  ];

  // Add published/modified time if provided
  if (metadata.publishedTime) {
    metaTags.push({
      property: "article:published_time",
      content: metadata.publishedTime,
    });
  }
  if (metadata.modifiedTime) {
    metaTags.push({
      property: "article:modified_time",
      content: metadata.modifiedTime,
    });
  }

  const blocks = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return {
    title: fullTitle,
    meta: metaTags,
    link: [{ rel: "canonical", href: fullUrl }],
    script: blocks.map((data, index) => ({
      key: `ld-json-${index}`,
      type: "application/ld+json",
      children: JSON.stringify(data),
    })),
  };
}

export function useSEO(
  metadata: SEOMetaData,
  structuredData?: StructuredData | StructuredData[]
) {
  useHead(buildSEOHead(metadata, structuredData));
}

// For pages whose SEO depends on data that loads after mount (property and
// room pages). Must be called during setup; the getter returns null until the
// data is ready.
export function useDynamicSEO(
  getter: () => {
    metadata: SEOMetaData;
    structuredData?: StructuredData | StructuredData[];
  } | null
) {
  useHead(
    computed(() => {
      const seo = getter();
      return seo ? buildSEOHead(seo.metadata, seo.structuredData) : {};
    })
  );
}

const LODGING_TYPES: Record<string, string> = {
  hotel: "Hotel",
  resort: "Resort",
  motel: "Motel",
  hostel: "Hostel",
};

export function generateHotelStructuredData(
  hotel: HotelDetails,
  options: { reviewCount?: number; ratingValue?: number } = {}
): StructuredData {
  const url = `${SITE_URL}/property/${hotel.id}`;
  const typeKey = (hotel.propertyTypeDesc || "").toLowerCase();

  const data: StructuredData = {
    "@context": "https://schema.org",
    "@type": LODGING_TYPES[typeKey] || "LodgingBusiness",
    "@id": url,
    name: hotel.name,
    url,
    description: hotel.description || `Book ${hotel.name} on Roomsvile`,
    image: (hotel.hotelImages || []).map((img) => toHttps(img.imageUrl)),
    address: {
      "@type": "PostalAddress",
      streetAddress: hotel.address,
      addressLocality: hotel.city?.name,
      addressRegion: hotel.state?.name,
      addressCountry: hotel.country?.code || "NG",
    },
    currenciesAccepted: "NGN",
  };

  if (hotel.phoneNumber) data.telephone = hotel.phoneNumber;
  if (hotel.price > 0) {
    data.priceRange = `From ₦${hotel.price.toLocaleString("en-NG")}`;
  }

  if (hotel.facilities?.length) {
    data.amenityFeature = hotel.facilities.map((facility) => ({
      "@type": "LocationFeatureSpecification",
      name: facility.name,
      value: true,
    }));
  }

  // Only claim a rating when it's backed by real reviews; a made-up count
  // can get rich results removed.
  const reviewCount = options.reviewCount ?? 0;
  const ratingValue = options.ratingValue ?? hotel.rating;
  if (reviewCount > 0 && ratingValue > 0) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(ratingValue.toFixed(1)),
      bestRating: 5,
      worstRating: 1,
      reviewCount,
    };
  }

  return data;
}

export function generateRoomStructuredData(room: RoomDetails): StructuredData {
  const url = `${SITE_URL}/room/${room.id}`;

  const data: StructuredData = {
    "@context": "https://schema.org",
    "@type": "HotelRoom",
    "@id": url,
    name: room.name,
    url,
    description: room.description,
    image: (room.images || []).map((img) => toHttps(img.imageUrl)),
  };

  if (room.numberOfGuests > 0) {
    data.occupancy = {
      "@type": "QuantitativeValue",
      maxValue: room.numberOfGuests,
    };
  }
  if (room.numberOfBeds > 0) {
    data.bed = { "@type": "BedDetails", numberOfBeds: room.numberOfBeds };
  }
  if (room.hotelName) {
    data.containedInPlace = {
      "@type": "LodgingBusiness",
      name: room.hotelName,
      ...(room.hotelId ? { url: `${SITE_URL}/property/${room.hotelId}` } : {}),
    };
  }
  if (room.price > 0) {
    data.offers = {
      "@type": "Offer",
      price: room.price,
      priceCurrency: "NGN",
      url,
    };
  }

  return data;
}

export function generateBreadcrumbStructuredData(
  items: Array<{ name: string; url: string }>
): StructuredData {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// Site-wide identity for Google's knowledge panel and brand searches
export function generateOrganizationStructuredData(): StructuredData {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.jpg`,
    email: "info@roomsvile.com",
    telephone: "+2348087102974",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Lagos",
      addressCountry: "NG",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+2348087102974",
      email: "info@roomsvile.com",
      contactType: "customer service",
      areaServed: "NG",
      availableLanguage: ["English"],
    },
  };
}
